import * as React from 'react';
import { Flex, isDarkThemeMixin, Text, useIsMobile } from '@amzn/storm-ui';
import styled, { css } from 'styled-components';
import {
  AccountGroup,
  AdvertisingAccountType,
} from '@amzn/aac-chrome-internal-api';
import { MessageBundle } from '@amzn/i18n.panther-resource-resolver';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { getCountryCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { AccountDropdownAccountName, IA2AccountDropdownButton } from './styles';
import { ACCOUNT_TYPE_PILL_LABEL_MAP } from './AccountTypeLocalizationKeyMapping';
import {
  getGlobalSponsoredAdsAccountFromAccountGroup,
  getSingleGlobalAccount,
} from './accountHelper';
import AccountNameValue from './AccountNameValue';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';

export type AccountDropdownTriggerProps = {
  handleToggle: () => void;
  isOpen: boolean;
};

type AccountDropdownTriggerCommonProps = {
  accountName?: string;
  marketplaceLabel: string;
  allAccountsLabel: string;
  isTriggerEnabled: boolean;
  handleToggle: () => void;
  isOpen: boolean;
};

const MarketplaceInfo = styled(Text)`
  color: ${({ theme }) => theme.text.color.unavailableInverted};
  text-wrap: wrap;

  ${isDarkThemeMixin(css`
    color: ${({ theme }) => theme.text.color.tertiary};
  `)}
`;

const TextContainer = styled.div`
  text-align: left;
  max-width: 100%;
`;

export const getAccountTypeLabel = ({
  accounts,
  currentEntityId,
  currentAccountType,
  currentMarketplaceId,
  bundle,
}: {
  accounts: AccountGroup[];
  currentEntityId: string;
  currentAccountType: AdvertisingAccountType;
  currentMarketplaceId: string;
  bundle: MessageBundle;
}): string => {
  const parentGlobalSponsoredAdsAccount =
    getGlobalSponsoredAdsAccountFromAccountGroup(
      currentEntityId,
      currentAccountType,
      accounts,
    );

  if (
    parentGlobalSponsoredAdsAccount?.nestedAccounts &&
    parentGlobalSponsoredAdsAccount.nestedAccounts.length > 1
  ) {
    return bundle.getMessage('account-switcher-pill-global-sa-label');
  }

  const singleGlobalAccount = getSingleGlobalAccount(accounts, currentEntityId);

  if (singleGlobalAccount) {
    return bundle.getMessage(
      'account-switcher-pill-global-single-global-label',
    );
  }

  const accountTypeLabel = ACCOUNT_TYPE_PILL_LABEL_MAP.get(currentAccountType);

  if (
    accountTypeLabel &&
    currentAccountType === AdvertisingAccountType.SPONSORED_ADS
  ) {
    const countryCode = getCountryCodeByMarketplace(currentMarketplaceId);
    return bundle.formatMessage(accountTypeLabel, {
      marketplaceName: countryCode
        ? bundle.getMessage(`account-switcher-marketplace-${countryCode}`)
        : '',
    });
  }

  if (accountTypeLabel) {
    return bundle.getMessage(accountTypeLabel);
  }

  return '';
};

export const AccountDropdownTriggerCommon: React.FC<
  AccountDropdownTriggerCommonProps
> = ({
  accountName,
  marketplaceLabel,
  allAccountsLabel,
  isTriggerEnabled,
  isOpen,
  handleToggle,
}) => {
  const chromeConfig = useChromeConfigContext();
  const isMobile = useIsMobile();
  const isCurrentScopeAllAccounts =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const supportedPageScopes = chromeConfig.pageHeader?.supportedPageScopes;
  const isSwitchable =
    Array.isArray(supportedPageScopes) && supportedPageScopes.length > 1;
  const showAllAccountsLabel =
    isCurrentScopeAllAccounts || (isSwitchable && !accountName);

  return (
    <IA2AccountDropdownButton
      disabled={!isTriggerEnabled}
      onClick={handleToggle}
      active={isOpen}
      data-ccx-e2e-id="aac-account-dropdown"
      aria-haspopup={true}
      aria-expanded={isOpen}
      {...getTaktAttributes(
        'account-switcher-trigger',
        chromeComponents.HEADER,
      )}
    >
      <Flex alignItems="start">
        {showAllAccountsLabel && (
          <TextContainer>
            <AccountDropdownAccountName>
              {allAccountsLabel}
            </AccountDropdownAccountName>
          </TextContainer>
        )}
        {!showAllAccountsLabel && (
          <TextContainer>
            <AccountNameValue accountName={accountName} isMobile={isMobile} />
            {!!accountName && (
              <MarketplaceInfo size="xsmall">
                {marketplaceLabel}
              </MarketplaceInfo>
            )}
          </TextContainer>
        )}
      </Flex>
    </IA2AccountDropdownButton>
  );
};
